import { useState, useCallback, useMemo } from "react";
import { ChartBar, Trophy, Fire, TrendUp, ArrowUp, MagnifyingGlass, X } from "@phosphor-icons/react";
import SEO from "@/components/SEO";
import AINavBar from "@/components/AINavBar";
import { useInfiniteFeed } from "@/hooks/useInfiniteFeed";
import { fetchAISection } from "@/lib/api";

const BENCHMARK_META = {
  MMLU:        { icon: "🧠", name: "Reasoning",  unit: "% accuracy", description: "57-subject knowledge test across STEM, humanities, and social sciences." },
  HumanEval:   { icon: "💻", name: "Coding",     unit: "% pass@1",   description: "164 programming challenges requiring correct code from docstrings." },
  MATH:        { icon: "📐", name: "Math",       unit: "% accuracy", description: "12,500 competition math problems from AMC and AIME." },
  GPQA:        { icon: "🔬", name: "Science",    unit: "% accuracy", description: "PhD-level science questions in biology, chemistry, and physics." },
  "SWE-bench": { icon: "🛠",  name: "SWE-bench", unit: "% resolved", description: "Real GitHub issues from popular open-source repositories." },
};

const providerColors = {
  OpenAI:        "bg-emerald-100 text-emerald-800",
  Anthropic:     "bg-purple-100 text-purple-800",
  Google:        "bg-blue-100 text-blue-800",
  Meta:          "bg-indigo-100 text-indigo-800",
  DeepSeek:      "bg-amber-100 text-amber-800",
  Mistral:       "bg-rose-100 text-rose-800",
  Alibaba:       "bg-orange-100 text-orange-800",
  Princeton:     "bg-cyan-100 text-cyan-800",
  "Cognition AI":"bg-violet-100 text-violet-800",
  xAI:           "bg-slate-200 text-slate-800",
};

function MedalIcon({ rank }) {
  if (rank === 1) return <span className="text-base">🥇</span>;
  if (rank === 2) return <span className="text-base">🥈</span>;
  if (rank === 3) return <span className="text-base">🥉</span>;
  return <span className="text-xs font-bold text-slate-500 w-5 text-center">{rank}</span>;
}

function ScoreBar({ score, max }) {
  const pct = Math.min(100, (score / (max || 100)) * 100);
  return (
    <div className="flex items-center gap-2 shrink-0">
      <div className="w-20 h-1.5 bg-slate-200 rounded-full overflow-hidden">
        <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-sm font-bold text-slate-900 w-10 text-right">{score}</span>
    </div>
  );
}

// Accepts `search` prop and filters its leaderboard rows
function BenchmarkTable({ category, search }) {
  const [showAll, setShowAll] = useState(false);

  const filtered = useMemo(() => {
    if (!search) return category.leaderboard;
    const q = search.toLowerCase();
    return category.leaderboard.filter(
      (e) => e.model?.toLowerCase().includes(q) || e.provider?.toLowerCase().includes(q)
    );
  }, [category.leaderboard, search]);

  const rows = showAll ? filtered : filtered.slice(0, 5);
  const topScore = (filtered[0] || category.leaderboard[0])?.score || 100;

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-md transition-shadow">
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 p-5 text-white">
        <div className="flex items-center gap-3 mb-1">
          <span className="text-2xl">{category.icon}</span>
          <div className="flex-1">
            <h3 className="text-base font-bold">{category.name}</h3>
            <p className="text-xs text-slate-400">{category.benchmark} · {category.unit}</p>
          </div>
          {search && (
            <span className="text-[10px] text-slate-400 shrink-0">
              {filtered.length} / {category.leaderboard.length}
            </span>
          )}
        </div>
        <p className="text-xs text-slate-400 leading-relaxed mt-2">{category.description}</p>
      </div>

      <div className="divide-y divide-slate-100">
        {rows.length === 0 ? (
          <div className="px-5 py-6 text-center text-xs text-slate-400">
            No models match "<strong>{search}</strong>"
          </div>
        ) : rows.map((entry, i) => (
          <div
            key={`${entry.model}-${i}`}
            className={`flex items-center gap-3 px-5 py-3 transition-colors ${i === 0 && !search ? "bg-amber-50/50" : "hover:bg-slate-50"}`}
          >
            <div className="w-8 flex items-center justify-center shrink-0">
              <MedalIcon rank={search ? i + 1 : category.leaderboard.indexOf(entry) + 1} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-slate-900 truncate">{entry.model}</span>
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${providerColors[entry.provider] || "bg-slate-100 text-slate-600"}`}>
                  {entry.provider}
                </span>
                {(entry.isNew || entry.is_new) && (
                  <span className="text-[10px] font-bold bg-emerald-500 text-white px-1.5 py-0.5 rounded">NEW</span>
                )}
              </div>
              {entry.date && (
                <p className="text-[10px] text-slate-400 mt-0.5">
                  {new Date(entry.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </p>
              )}
            </div>
            <ScoreBar score={entry.score} max={topScore * 1.05} />
          </div>
        ))}
      </div>

      {filtered.length > 5 && (
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50">
          <button onClick={() => setShowAll((v) => !v)} className="text-xs font-medium text-blue-600 hover:text-blue-700">
            {showAll ? "Show less" : `Show all ${filtered.length} models`}
          </button>
        </div>
      )}
    </div>
  );
}

function SummaryLeader({ categories }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
      {categories.map((cat) => {
        const leader = cat.leaderboard[0];
        if (!leader) return null;
        return (
          <div key={cat.id} className="bg-white border border-slate-200 rounded-xl p-3 text-center hover:border-blue-300 transition-colors">
            <div className="text-2xl mb-1">{cat.icon}</div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">{cat.name}</div>
            <div className="text-xs font-bold text-slate-900 leading-tight">{leader.model}</div>
            <div className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full mt-1 inline-block ${providerColors[leader.provider] || "bg-slate-100 text-slate-600"}`}>
              {leader.score}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function buildCategories(items) {
  const byBenchmark = {};
  for (const item of items) {
    const bName = item.benchmark;
    if (!bName) continue;
    if (!byBenchmark[bName]) byBenchmark[bName] = [];
    byBenchmark[bName].push(item);
  }
  return Object.entries(byBenchmark).map(([bName, entries]) => {
    const meta = BENCHMARK_META[bName] || { icon: "📊", name: bName, unit: "score", description: "" };
    return {
      id: bName.toLowerCase().replace(/[^a-z0-9]/g, "-"),
      name: meta.name,
      icon: meta.icon,
      benchmark: bName,
      unit: meta.unit,
      description: meta.description,
      leaderboard: [...entries].sort((a, b) => b.score - a.score),
    };
  });
}

export default function AIBenchmarksPage() {
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");

  const fetchFn = useCallback(
    (page) => fetchAISection("benchmarks", { page, page_size: 50 }),
    []
  );
  const { items: apiItems, loading, error } = useInfiniteFeed({
    fetchFn,
    feedKey: "ai-benchmarks",
    getCacheKey: (page) => `ai-benchmarks-p${page}`,
    ttlMs: 15 * 60 * 1000,
  });

  const categories = useMemo(() => {
    if (apiItems.length > 0) return buildCategories(apiItems);
    return [];
  }, [apiItems]);

  const displayed = activeTab === "all" ? categories : categories.filter((c) => c.id === activeTab);

  const modelScores = useMemo(() => {
    const map = {};
    categories.forEach((cat) => {
      const max = cat.leaderboard[0]?.score || 100;
      cat.leaderboard.forEach((entry) => {
        const key = `${entry.model}__${entry.provider}`;
        if (!map[key]) map[key] = { model: entry.model, provider: entry.provider, total: 0, count: 0, isNew: entry.isNew || entry.is_new };
        map[key].total += (entry.score / max) * 100;
        map[key].count += 1;
      });
    });
    return Object.values(map)
      .map((m) => ({ ...m, avg: m.total / m.count }))
      .sort((a, b) => b.avg - a.avg)
      .slice(0, 8);
  }, [categories]);

  const filteredScores = useMemo(() => {
    if (!search) return modelScores;
    const q = search.toLowerCase();
    return modelScores.filter(
      (m) => m.model?.toLowerCase().includes(q) || m.provider?.toLowerCase().includes(q)
    );
  }, [modelScores, search]);

  return (
    <>
      <SEO
        title="AI Benchmarks — Model Leaderboards & Rankings · TrendMing"
        description="Live AI benchmark leaderboards: MMLU reasoning, HumanEval coding, MATH, GPQA expert knowledge, and SWE-bench agent performance."
        canonical="https://trendming.com/trending-ai/benchmarks"
      />
      <AINavBar />
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">

        {/* Header + search bar */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">AI Intelligence · Benchmarks</span>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mt-1">AI Benchmark Center</h1>
            <p className="text-slate-500 text-sm mt-1">Live leaderboards across reasoning, coding, math, expert knowledge, and agent performance.</p>
          </div>
          <div className="relative w-full sm:w-64 shrink-0">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by model or provider…"
              className="w-full pl-9 pr-8 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <X size={13} />
              </button>
            )}
          </div>
        </div>

        {loading && (
          <div className="flex items-center gap-3 mb-8 p-4 bg-slate-50 rounded-xl text-xs text-slate-400 animate-pulse">
            <div className="w-4 h-4 bg-slate-200 rounded-full" />
            Loading latest benchmark data…
          </div>
        )}
        {error && !loading && (
          <div className="text-center py-8 text-slate-500">
            <p className="text-red-500 font-medium">Could not load benchmarks</p>
            <p className="text-xs mt-1">Content is being refreshed — check back shortly.</p>
          </div>
        )}
        {!loading && !error && categories.length === 0 && (
          <div className="text-center py-8 text-slate-400">
            <p className="font-medium">Benchmark data is being collected</p>
            <p className="text-xs mt-1">Run benchmark jobs from the admin panel to populate data.</p>
          </div>
        )}

        {categories.length > 0 && (
          <>
            <div className="mb-2">
              <h2 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Trophy size={14} className="text-amber-500" weight="fill" />
                Current Leaders by Category
              </h2>
              <SummaryLeader categories={categories} />
            </div>

            {filteredScores.length > 0 && (
              <div className="bg-gradient-to-br from-slate-900 to-blue-950 rounded-2xl p-6 text-white mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <TrendUp size={16} weight="fill" className="text-blue-400" />
                  <h2 className="text-base font-bold">Overall Model Ranking</h2>
                  <span className="text-xs text-slate-400">(avg. normalized score across all benchmarks)</span>
                  {search && <span className="ml-auto text-xs text-slate-400">{filteredScores.length} result{filteredScores.length !== 1 ? "s" : ""}</span>}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {filteredScores.map((m, i) => (
                    <div key={m.model} className={`rounded-xl p-3 ${i === 0 && !search ? "bg-amber-500/20 border border-amber-500/30" : "bg-white/5 border border-white/10"}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <MedalIcon rank={i + 1} />
                        {m.isNew && <span className="text-[9px] font-bold bg-emerald-500 text-white px-1 py-0.5 rounded">NEW</span>}
                      </div>
                      <div className="text-xs font-bold text-white leading-tight">{m.model}</div>
                      <div className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full mt-1 inline-block ${providerColors[m.provider] || "bg-white/10 text-white"}`}>
                        {m.provider}
                      </div>
                      <div className="text-base font-black text-white mt-1">{m.avg.toFixed(1)}</div>
                      <div className="text-[10px] text-slate-400">avg. score</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {search && filteredScores.length === 0 && (
              <div className="bg-slate-100 rounded-xl px-5 py-4 mb-8 text-sm text-slate-500">
                No models match "<strong className="text-slate-700">{search}</strong>" in the overall ranking.
              </div>
            )}

            <div className="flex items-center gap-2 flex-wrap mb-6">
              <button
                onClick={() => setActiveTab("all")}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${activeTab === "all" ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
              >
                All Benchmarks
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveTab(cat.id)}
                  className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${activeTab === cat.id ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                >
                  {cat.icon} {cat.name}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {displayed.map((cat) => <BenchmarkTable key={cat.id} category={cat} search={search} />)}
            </div>
          </>
        )}

        <div className="mt-8 bg-slate-50 border border-slate-200 rounded-2xl p-5">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">About These Benchmarks</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-slate-500 leading-relaxed">
            <div>
              <p className="font-semibold text-slate-700 mb-1">MMLU (Massive Multitask Language Understanding)</p>
              <p>Tests knowledge across 57 subjects including STEM, humanities, and social sciences. 14,000+ questions.</p>
            </div>
            <div>
              <p className="font-semibold text-slate-700 mb-1">HumanEval (Coding)</p>
              <p>164 hand-crafted programming challenges. Measures ability to produce correct code from docstrings.</p>
            </div>
            <div>
              <p className="font-semibold text-slate-700 mb-1">MATH</p>
              <p>12,500 competition math problems from AMC, AIME, and AMC 10/12. Tests advanced mathematical reasoning.</p>
            </div>
            <div>
              <p className="font-semibold text-slate-700 mb-1">SWE-bench Verified</p>
              <p>Real GitHub issues from popular open-source repos. Measures end-to-end software engineering capability.</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
